import { useState, useCallback, useEffect } from 'react';
import { BeaconWallet } from '@taquito/beacon-wallet';
import {
  requestNonce,
  verifySignature,
  storeToken,
  getStoredToken,
  clearStoredToken,
  isTokenExpired,
} from '@/lib/auth';

interface AuthState {
  isAuthenticated: boolean;
  isAuthenticating: boolean;
  error: string | null;
}

export function useAuth(wallet: BeaconWallet | null, walletAddress: string | null) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isAuthenticating: false,
    error: null,
  });

  // Check for existing valid token on mount and wallet change
  useEffect(() => {
    // Don't do anything until wallet address is loaded
    // (walletAddress being null means wallet is still initializing)
    if (walletAddress === null) {
      return;
    }

    const token = getStoredToken();
    if (token && !isTokenExpired(token)) {
      // Verify token belongs to current wallet
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.wallet === walletAddress) {
            setState({ isAuthenticated: true, isAuthenticating: false, error: null });
            return;
          }
          // Token belongs to different wallet - clear it
          clearStoredToken();
        }
      } catch {
        // Invalid token format - clear it
        clearStoredToken();
      }
    }

    // No valid token for this wallet
    setState({ isAuthenticated: false, isAuthenticating: false, error: null });
  }, [walletAddress]);

  const authenticate = useCallback(async () => {
    if (!wallet || !walletAddress) {
      setState((prev) => ({ ...prev, error: 'Wallet not connected' }));
      return false;
    }

    setState({ isAuthenticated: false, isAuthenticating: true, error: null });

    try {
      // Step 1: Request nonce from server
      const { nonce, message } = await requestNonce(walletAddress);

      // Step 2: Sign the message with wallet
      // Using Beacon's requestSignPayload
      const signResult = await wallet.client.requestSignPayload({
        signingType: 'micheline',
        payload: stringToHex(message),
      });

      // Step 3: Verify signature and get JWT
      const { token } = await verifySignature(
        walletAddress,
        signResult.signingType === 'micheline' ? '' : '', // Public key not needed for simplified auth
        signResult.signature,
        nonce
      );

      // Step 4: Store token
      storeToken(token);

      setState({ isAuthenticated: true, isAuthenticating: false, error: null });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Authentication failed';
      setState({ isAuthenticated: false, isAuthenticating: false, error: message });
      return false;
    }
  }, [wallet, walletAddress]);

  const logout = useCallback(() => {
    clearStoredToken();
    setState({ isAuthenticated: false, isAuthenticating: false, error: null });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    authenticate,
    logout,
    clearError,
  };
}

// Helper to convert string to hex for Beacon signing (browser-compatible)
function stringToHex(str: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let hex = '05'; // Micheline bytes prefix
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}
